import { useEffect, useState } from 'react';
import { fetchImage } from '../lib/api';
import { signatureApi } from '../lib/signatures';

/**
 * A staff line on a printed document — "Prepared by (office)", "Approved by
 * (owner)", "Paid by", "Checked by" — carrying the saved signature of the
 * person who did it, with their name and the time they did it.
 *
 * The signature is the one they had saved at that moment (the Worker keeps
 * every version), fetched with the session token. When there is nobody yet
 * (not approved) or they have never saved a signature, the line stays a line
 * to sign by hand — with their name under it when it is known.
 */

interface Props {
  label: string;
  userId?: string | null | undefined;
  name?: string | null | undefined;
  /** When they did it: picks the signature in force then, and is printed. */
  at?: string | null | undefined;
}

export default function StaffStamp({ label, userId, name, at }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [done, setDone] = useState(!userId);

  useEffect(() => {
    if (!userId) return;
    let live = true;
    let objectUrl: string | null = null;
    fetchImage(signatureApi.userPath(userId, at))
      .then((blob) => {
        if (!live) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setDone(true);
      })
      .catch(() => { if (live) setDone(true); });
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [userId, at]);

  const when = at ? new Date(at).toLocaleString('en-GB') : null;

  return (
    <div className="stamp-line">
      <div>{label}:</div>
      {url ? (
        <img src={url} alt={`signature of ${name ?? ''}`} className="stamp-sig" />
      ) : (
        <div className="stamp-blank">{done ? '' : '…'}</div>
      )}
      {name && (
        <div className="stamp-who">
          {name}
          {when && ` · ${when}`}
        </div>
      )}
    </div>
  );
}
